import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, User, FileText, Edit, Calendar, Phone, Mail, MapPin, UserCheck, CreditCard, X, AlertCircle, Plus, Minus, RotateCcw } from 'lucide-react';
import patientService from '../../patient/services/patientService';

const ClinicianPatientFilesPage = () => {
  const navigate = useNavigate();
  
  // State for search inputs
  const [searchData, setSearchData] = useState({
    patientId: '',
    patientName: '',
    patientDOB: ''
  });

  // State for patient search and selection
  const [showPatientIdDropdown, setShowPatientIdDropdown] = useState(false);
  const [showPatientNameDropdown, setShowPatientNameDropdown] = useState(false);
  const [filteredPatientsById, setFilteredPatientsById] = useState([]);
  const [filteredPatientsByName, setFilteredPatientsByName] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [isPatientSelected, setIsPatientSelected] = useState(false);

  // State for real patient data from API
  const [allPatients, setAllPatients] = useState([]);
  const [loadingPatients, setLoadingPatients] = useState(false);

  // State for toast notifications
  const [toast, setToast] = useState(null);

  // State for collapsible search section (open by default on Patient Files page)
  const [isSearchExpanded, setIsSearchExpanded] = useState(true);

  // Set page title and fetch patients
  useEffect(() => {
    document.title = 'MediFusion - Patient Files';
    fetchAllPatients();
  }, []);

  // Fetch all patients from the database
  const fetchAllPatients = async () => {
    try {
      setLoadingPatients(true);
      console.log('🔍 Fetching all patients from database...');
      const response = await patientService.getAllPatients();
      console.log('👥 Patients from API:', response);
      
      // The response should have a patients array and metadata
      const patients = response.patients || response || [];
      setAllPatients(patients);
      console.log('✅ Loaded', patients.length, 'patients from database');
    } catch (error) {
      console.error('❌ Error fetching patients:', error);
      // Fallback to empty array if API fails
      setAllPatients([]);
    } finally {
      setLoadingPatients(false);
    }
  };

  // Filter patients by ID
  const filterPatientsById = (searchTerm) => {
    if (!searchTerm.trim()) {
      setFilteredPatientsById([]);
      return;
    }

    const filtered = allPatients.filter(patient => 
      patient.patient_id && patient.patient_id.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredPatientsById(filtered);
  };

  // Filter patients by name
  const filterPatientsByName = (searchTerm) => {
    if (!searchTerm.trim()) {
      setFilteredPatientsByName([]);
      return;
    }

    const filtered = allPatients.filter(patient => 
      `${patient.first_name} ${patient.last_name}`.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredPatientsByName(filtered);
  };

  // Handle patient ID input change
  const handlePatientIdChange = (value) => {
    setSearchData(prev => ({ ...prev, patientId: value }));
    filterPatientsById(value);
    setShowPatientIdDropdown(true);
    setShowPatientNameDropdown(false);
  };

  // Handle patient name input change
  const handlePatientNameChange = (value) => {
    setSearchData(prev => ({ ...prev, patientName: value }));
    filterPatientsByName(value);
    setShowPatientNameDropdown(true);
    setShowPatientIdDropdown(false);
  };

  // Handle patient selection from dropdown
  const handlePatientSelect = (patient, searchType = 'mixed') => {
    setSelectedPatient(patient);
    
    // Format DOB for the date input field
    let formattedDOB = '';
    if (patient.dob && patient.dob !== '1900-01-01' && patient.dob !== null) {
      formattedDOB = patient.dob; // API already returns YYYY-MM-DD format
      console.log(`✅ DOB available for ${patient.first_name} ${patient.last_name}: ${patient.dob}`);
    } else {
      console.log(`⚠️ No valid DOB found for ${patient.first_name} ${patient.last_name}`);
    }
    
    // Update search data based on search type to prevent auto-population
    if (searchType === 'id') {
      // Patient ID search - only populate ID, don't auto-populate name/DOB
      setSearchData(prev => ({
        ...prev,
        patientId: patient.patient_id
        // Keep existing patientName and patientDOB values
      }));
    } else if (searchType === 'name') {
      // Patient name search - only populate name, don't auto-populate ID/DOB
      setSearchData(prev => ({
        ...prev,
        patientName: `${patient.first_name} ${patient.last_name}`
        // Keep existing patientId and patientDOB values
      }));
    } else {
      // Mixed/legacy behavior - populate all fields
      setSearchData({
        patientId: patient.patient_id,
        patientName: `${patient.first_name} ${patient.last_name}`,
        patientDOB: formattedDOB
      });
    }
    
    setIsPatientSelected(true);
    setShowPatientIdDropdown(false);
    setShowPatientNameDropdown(false);
    setFilteredPatientsById([]);
    setFilteredPatientsByName([]);
  };

  // Show toast notification
  const showToast = (message, type = 'error') => {
    setToast({ message, type });
    // Auto-hide toast after 4 seconds
    setTimeout(() => {
      setToast(null);
    }, 4000);
  };

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    
    // Dual search logic: Patient ID only OR Patient Name + DOB
    const hasPatientId = searchData.patientId.trim();
    const hasPatientName = searchData.patientName.trim();
    const hasPatientDOB = searchData.patientDOB.trim();
    
    // Search Method 1: Patient ID only
    if (hasPatientId && !hasPatientName && !hasPatientDOB) {
      console.log('🔍 Searching by Patient ID only:', searchData.patientId);
      
      // Find patient by ID
      const patient = allPatients.find(p => p.patient_id === searchData.patientId.trim());
      if (patient) {
        console.log('✅ Patient found by ID! Navigating to patient details...');
        navigate(`/clinician/patientfiles/${patient.patient_id}/general_details`);
      } else {
        showToast('Patient not found with this ID', 'error');
      }
      return;
    }
    
    // Search Method 2: Patient Name + DOB combination
    if (hasPatientName && hasPatientDOB) {
      console.log('🔍 Searching by Patient Name + DOB:', { name: searchData.patientName, dob: searchData.patientDOB });
      
      // Find patient by name and DOB
      const patient = allPatients.find(p => {
        const fullName = `${p.first_name} ${p.last_name}`.toLowerCase();
        const nameMatch = fullName.includes(searchData.patientName.toLowerCase());
        const dobMatch = p.dob === searchData.patientDOB;
        return nameMatch && dobMatch;
      });
      
      if (patient) {
        console.log('✅ Patient found by Name + DOB! Navigating to patient details...');
        navigate(`/clinician/patientfiles/${patient.patient_id}/general_details`);
      } else {
        showToast('Patient not found with this name and date of birth combination', 'error');
      }
      return;
    }
    
    // Invalid search combinations
    if (hasPatientId && (hasPatientName || hasPatientDOB)) {
      showToast('Please search by Patient ID only, or by Patient Name + DOB combination', 'error');
      return;
    }
    
    if (hasPatientName && !hasPatientDOB) {
      showToast('When searching by name, please also enter the date of birth', 'error');
      return;
    }
    
    if (!hasPatientId && !hasPatientName) {
      showToast('Please enter either a Patient ID or Patient Name to search', 'error');
      return;
    }
  };

  // Handle new patient button
  const handleNewPatient = () => {
    setSearchData({
      patientId: '',
      patientName: '',
      patientDOB: ''
    });
    setSelectedPatient(null);
    setIsPatientSelected(false);
    setShowPatientIdDropdown(false);
    setShowPatientNameDropdown(false);
    setFilteredPatientsById([]);
    setFilteredPatientsByName([]);
  };

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      {/* Top Banner */}
      <div className="bg-blue-600 text-white px-6 py-1.5 rounded-lg mb-6">
        <h1 className="text-lg font-semibold text-center">Patient Files</h1>
      </div>

      {/* White Content Container */}
      <div className={`bg-white rounded-lg shadow-md ${isSearchExpanded ? "p-4" : "p-3"}`}>
        {/* Search Section */}
        <div className={isSearchExpanded ? "mb-4" : "mb-2"}>
          {/* Search Header with Icon and Toggle */}
          <div className={`flex items-center justify-between ${isSearchExpanded ? "mb-2" : "mb-0"}`}>
            <div className="flex items-center space-x-2">
              <Search className="h-5 w-5 text-blue-600" />
              <h2 className="text-lg font-bold text-gray-800">Patient Search</h2>
            </div>
            <button
              type="button"
              onClick={() => setIsSearchExpanded(!isSearchExpanded)}
              className={`bg-blue-500 hover:bg-blue-600 text-white rounded-lg flex items-center justify-center transition-colors shadow-sm ${isSearchExpanded ? "w-8 h-8" : "w-7 h-7"}`}
            >
              {isSearchExpanded ? (
                <Minus className="h-4 w-4" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
            </button>
          </div>
          
          {/* Collapsible Content */}
          {isSearchExpanded && (
            <div>
              {/* Blue stylish divider */}
              <div className="h-0.5 bg-gradient-to-r from-blue-400 to-blue-600 rounded-full mb-4"></div>
              
              {/* Search Form */}
              <form onSubmit={handleSearch} className="space-y-2">
            {/* Input Fields and Buttons Row - All in One Line */}
            <div className="flex items-end space-x-3">
              {/* Patient ID Input */}
              <div className="flex-1 relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">Patient ID:</label>
                <input
                  type="text"
                  placeholder="Enter Patient ID"
                  value={searchData.patientId}
                  onChange={(e) => handlePatientIdChange(e.target.value)}
                  onFocus={() => setShowPatientIdDropdown(true)}
                  disabled={isPatientSelected}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed text-sm"
                />
                
                {/* Dropdown for patient selection by ID */}
                {showPatientIdDropdown && filteredPatientsById.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
                    {filteredPatientsById.map((patient) => (
                      <div
                        key={patient.id || patient.patient_id}
                        onClick={() => handlePatientSelect(patient, 'id')}
                        className="px-4 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
                      >
                        <div className="font-medium text-gray-900">{patient.patient_id}</div>
                        <div className="text-sm text-gray-600">{patient.first_name} {patient.last_name}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Patient Name Input */}
              <div className="flex-1 relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">Patient Name:</label>
                <input
                  type="text"
                  placeholder="Enter Patient Name"
                  value={searchData.patientName}
                  onChange={(e) => handlePatientNameChange(e.target.value)}
                  onFocus={() => setShowPatientNameDropdown(true)}
                  disabled={isPatientSelected}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed text-sm"
                />
                
                {/* Dropdown for patient selection by name */}
                {showPatientNameDropdown && filteredPatientsByName.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
                    {filteredPatientsByName.map((patient) => (
                      <div
                        key={patient.id || patient.patient_id}
                        onClick={() => handlePatientSelect(patient, 'name')}
                        className="px-4 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
                      >
                        <div className="font-medium text-gray-900">{patient.first_name} {patient.last_name}</div>
                        <div className="text-sm text-gray-500">ID: {patient.patient_id}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Patient DOB Input */}
              <div className="w-48">
                <label className="block text-sm font-medium text-gray-700 mb-1">Patient DOB:</label>
                <input
                  type="date"
                  value={searchData.patientDOB}
                  onChange={(e) => setSearchData(prev => ({ ...prev, patientDOB: e.target.value }))}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors text-sm"
                />
              </div>
              
              {/* Search Button */}
              <button
                type="submit"
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-1.5 rounded-md transition-colors duration-200 flex items-center space-x-2 shadow-sm hover:shadow-md h-8 text-sm"
              >
                <Search className="h-3 w-3" />
                <span>Search</span>
              </button>
              
              {/* Reset Fields Button */}
              <button
                type="button"
                onClick={handleNewPatient}
                className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-4 py-1.5 rounded-md transition-all duration-200 flex items-center space-x-2 shadow-sm hover:shadow-md h-8 text-sm"
              >
                <RotateCcw className="h-3 w-3" />
                <span>Reset Fields</span>
              </button>
            </div>
          </form>
            </div>
          )}
        </div>

        {/* Empty Display Area */}
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
          <div className="text-gray-400 mb-4">
            <FileText className="h-16 w-16 mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-gray-500 mb-2">No Patient Files Found</h3>
          <p className="text-gray-400">Use the search above to find patient files or create a new patient</p>
        </div>
      </div>

      {/* Toast Notification */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-6 py-4 rounded-lg shadow-lg flex items-center space-x-3 transition-all duration-300 transform ${
          toast.type === 'error' 
            ? 'bg-red-500 text-white' 
            : 'bg-green-500 text-white'
        }`}>
          <AlertCircle className="h-5 w-5 flex-shrink-0" />
          <span className="font-medium">{toast.message}</span>
          <button
            onClick={() => setToast(null)}
            className="ml-2 hover:bg-black hover:bg-opacity-20 rounded-full p-1 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default ClinicianPatientFilesPage;